// Rentas: pestañas Activas / Por vencer / Morosos + Cambio de equipo (swap).
// Datos: /admin/assets/rentals* (mcp_django). Tokens de tema.

import { useEffect, useMemo, useState } from 'react';

import { createRental, debtors, listAssets, listRentals, rentalsDue, returnRental, swapAsset } from '../api';
import type { Asset, Rental, RentalStatus } from '../types';
import { RENTAL_STATUS_LABEL } from '../types';

type Tab = 'activas' | 'due' | 'morosos';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'activas', label: 'Activas' },
  { key: 'due', label: 'Por vencer' },
  { key: 'morosos', label: 'Morosos' },
];

function StatusBadge({ status }: { status: RentalStatus }) {
  const cls: Record<RentalStatus, string> = {
    active: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
    overdue: 'bg-red-500/15 text-red-600 dark:text-red-400',
    reserved: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    quote: 'bg-muted text-muted-foreground',
    returned: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    cancelled: 'bg-muted text-muted-foreground',
  };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}>{RENTAL_STATUS_LABEL[status]}</span>;
}

export function RentalsPage() {
  const [tab, setTab] = useState<Tab>('activas');
  const [rentals, setRentals] = useState<Rental[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [swapFor, setSwapFor] = useState<Rental | null>(null);
  const [creating, setCreating] = useState(false);
  const [returnFor, setReturnFor] = useState<Rental | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = tab === 'due' ? await rentalsDue(3)
        : tab === 'morosos' ? await debtors()
        : await listRentals({ status: 'active' });
      setRentals(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando rentas');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const totalDebt = useMemo(
    () => (rentals || []).reduce((s, r) => s + (r.balance || 0), 0),
    [rentals],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={() => setCreating(true)}
          className="ml-auto mb-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          + Nueva renta
        </button>
      </div>

      {tab === 'morosos' && rentals && rentals.length > 0 ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-2 text-sm text-foreground">
          Saldo pendiente total: <span className="font-semibold text-red-600 dark:text-red-400">${totalDebt.toLocaleString('es-MX')}</span> en {rentals.length} rentas.
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">Cliente</th>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 font-medium">Equipos</th>
              <th className="px-3 py-2 font-medium">Inicio</th>
              <th className="px-3 py-2 font-medium">Vence</th>
              <th className="px-3 py-2 font-medium text-right">Saldo</th>
              <th className="px-3 py-2 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && !rentals ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Cargando…</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-red-500">{error}</td></tr>
            ) : !rentals || rentals.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                {tab === 'due' ? 'No hay rentas por vencer.' : tab === 'morosos' ? 'Sin morosos. 🎉' : 'No hay rentas activas.'}
              </td></tr>
            ) : (
              rentals.map((r) => {
                const activeLines = (r.lines || []).filter((l) => l.active);
                const equipos = activeLines.length
                  ? activeLines.map((l) => l.asset_name).filter(Boolean)
                  : (r.lines || []).map((l) => l.asset_name).filter(Boolean);
                return (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2 font-medium text-foreground">{r.client_name || '—'}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 text-foreground">
                      {equipos.length ? (
                        <>
                          <div>{equipos[0]}{equipos.length > 1 ? ` +${equipos.length - 1}` : ''}</div>
                          <div className="text-xs text-muted-foreground">{activeLines.length || (r.lines || []).length} equipo(s)</div>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.start_date || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.end_date || '—'}</td>
                    <td className="px-3 py-2 text-right text-foreground">
                      {r.balance != null && r.balance > 0
                        ? <span className="font-medium text-red-600 dark:text-red-400">${r.balance.toLocaleString('es-MX')}</span>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {activeLines.length ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setSwapFor(r)}
                            className="rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted/50"
                          >
                            Cambiar equipo
                          </button>
                          <button
                            onClick={() => setReturnFor(r)}
                            className="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90"
                          >
                            Regresar equipo
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {returnFor ? (
        <ReturnModal rental={returnFor} onClose={() => setReturnFor(null)} onDone={() => { setReturnFor(null); load(); }} />
      ) : null}

      {creating ? (
        <NewRentalModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />
      ) : null}

      {swapFor ? (
        <SwapModal rental={swapFor} onClose={() => setSwapFor(null)} onDone={() => { setSwapFor(null); load(); }} />
      ) : null}
    </div>
  );
}

function SwapModal({ rental, onClose, onDone }: { rental: Rental; onClose: () => void; onDone: () => void }) {
  const activeLines = (rental.lines || []).filter((l) => l.active && l.asset_id);
  const [oldAssetId, setOldAssetId] = useState(activeLines[0]?.asset_id || '');
  const [newAssetId, setNewAssetId] = useState('');
  const [reason, setReason] = useState('');
  const [available, setAvailable] = useState<Asset[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listAssets({ status: 'available', limit: 1000 }).then(setAvailable).catch(() => setAvailable([]));
  }, []);

  async function submit() {
    if (!oldAssetId || !newAssetId) { setErr('Elige el equipo que sale y el que entra.'); return; }
    setSaving(true);
    setErr(null);
    try {
      await swapAsset(rental.id, { old_asset_id: oldAssetId, new_asset_id: newAssetId, reason: reason.trim() });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo hacer el cambio');
    } finally {
      setSaving(false);
    }
  }

  const field = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-lg font-semibold text-foreground">Cambio de equipo</h3>
        <p className="mb-3 text-sm text-muted-foreground">Renta de {rental.client_name || 'cliente'}. El equipo viejo regresa al inventario y el nuevo sale al cliente, conservando el historial.</p>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Equipo que sale</label>
            <select className={field} value={oldAssetId} onChange={(e) => setOldAssetId(e.target.value)}>
              {activeLines.map((l) => (
                <option key={l.asset_id!} value={l.asset_id!}>{l.asset_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Equipo que entra (disponibles)</label>
            <select className={field} value={newAssetId} onChange={(e) => setNewAssetId(e.target.value)}>
              <option value="">Elegir…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.category || a.name} {a.identifier ? `· ${a.identifier}` : ''}</option>
              ))}
            </select>
          </div>
          <input className={field} placeholder="Motivo (falla, mantenimiento, upgrade…)" value={reason} onChange={(e) => setReason(e.target.value)} />
          {err ? <div className="text-sm text-red-500">{err}</div> : null}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/50">Cancelar</button>
          <button onClick={submit} disabled={saving} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {saving ? 'Cambiando…' : 'Confirmar cambio'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Alta de renta: cliente → equipos → periodo → PRECIO NEGOCIADO.
 *
 * Por qué el precio se captura aquí y no sale de un catálogo: rentalmaq no
 * maneja lista de precios fija, se negocia por trato. Las 11 rentas migradas
 * entraron sin tarifa y por eso el BI de flota reporta ingreso $0 con equipo
 * afuera. Capturar `rate_amount` por línea es lo que destraba esa métrica.
 */
function NewRentalModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [clientName, setClientName] = useState('');
  const [rateUnit, setRateUnit] = useState('day');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [deposit, setDeposit] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('active');
  const [available, setAvailable] = useState<Asset[]>([]);
  const [lines, setLines] = useState<Array<{ asset_id: string; qty: number; rate_amount: string }>>([
    { asset_id: '', qty: 1, rate_amount: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listAssets({ status: 'available', limit: 1000 })
      .then(setAvailable)
      .catch(() => setAvailable([]));
  }, []);

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l.rate_amount) || 0) * (Number(l.qty) || 1), 0),
    [lines],
  );

  function setLine(i: number, patch: Partial<{ asset_id: string; qty: number; rate_amount: string }>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function submit() {
    const picked = lines.filter((l) => l.asset_id);
    if (!clientName.trim()) { setErr('Escribe el nombre del cliente.'); return; }
    if (picked.length === 0) { setErr('Elige al menos un equipo.'); return; }
    setSaving(true);
    setErr(null);
    try {
      await createRental({
        client_name: clientName.trim(),
        assets: picked.map((l) => ({
          asset_id: l.asset_id,
          qty: Number(l.qty) || 1,
          // null (no 0) cuando no se capturó: 0 significaría "gratis".
          rate_amount: l.rate_amount === '' ? null : Number(l.rate_amount),
          rate_unit: rateUnit,
        })),
        rate_unit: rateUnit,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        deposit: deposit === '' ? null : Number(deposit),
        total: total || null,
        notes: notes.trim(),
        status,
      });
      onDone();
    } catch (e) {
      // El backend rechaza status=active si el cliente trae expediente
      // incompleto o saldo vencido (regla dura F4). Se muestra tal cual para
      // que el vendedor sepa que puede dejarla en cotización/reserva.
      setErr(e instanceof Error ? e.message : 'No se pudo crear la renta');
    } finally {
      setSaving(false);
    }
  }

  const field = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-lg font-semibold text-foreground">Nueva renta</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Los equipos salen del inventario al guardar. El precio se captura por equipo porque se negocia por trato.
        </p>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Cliente</label>
              <input className={field} value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Nombre o razón social" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Tipo de renta</label>
              <select className={field} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Salida (el equipo sale hoy)</option>
                <option value="reserved">Reserva</option>
                <option value="quote">Cotización</option>
              </select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Inicio</label>
              <input type="date" className={field} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Fin (vencimiento)</label>
              <input type="date" className={field} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Tarifa por</label>
              <select className={field} value={rateUnit} onChange={(e) => setRateUnit(e.target.value)}>
                <option value="day">Día</option>
                <option value="week">Semana</option>
                <option value="month">Mes</option>
              </select>
            </div>
          </div>
          {!endDate ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Sin fecha de fin la renta se trata como indefinida y el equipo no se libera solo del calendario.
            </p>
          ) : null}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Equipos ({available.length} disponibles)</label>
              <button
                onClick={() => setLines((p) => [...p, { asset_id: '', qty: 1, rate_amount: '' }])}
                className="text-xs font-medium text-primary hover:underline"
              >
                + Agregar equipo
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <select className={`${field} col-span-6`} value={l.asset_id} onChange={(e) => setLine(i, { asset_id: e.target.value })}>
                    <option value="">— elige equipo —</option>
                    {available.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} · {a.category}</option>
                    ))}
                  </select>
                  <input type="number" min={1} className={`${field} col-span-2`} value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} />
                  <input
                    type="number" min={0} step="0.01"
                    className={`${field} col-span-3`}
                    value={l.rate_amount}
                    onChange={(e) => setLine(i, { rate_amount: e.target.value })}
                    placeholder="Precio"
                  />
                  {lines.length > 1 ? (
                    <button onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 text-destructive" title="Quitar">×</button>
                  ) : <span className="col-span-1" />}
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Depósito / garantía</label>
              <input type="number" min={0} step="0.01" className={field} value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Total estimado</label>
              <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm font-semibold text-foreground">
                ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Notas</label>
            <textarea className={field} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {err ? <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div> : null}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted">Cancelar</button>
            <button
              onClick={submit}
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Crear renta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Regreso de equipo (RentalOS F3) — cierra el ciclo salida → regreso.
 *
 * Aquí se captura el horómetro porque es EL momento en que se toma la lectura:
 * la máquina acaba de volver de obra y está enfrente. El backend acumula las
 * horas y puede disparar el servicio automático; si se deja para después,
 * simplemente no se captura (las 74 unidades en 0 h son la prueba).
 *
 * Se pueden regresar equipos parciales: en una renta de varios, el cliente
 * suele devolver de uno en uno.
 */
function ReturnModal({ rental, onClose, onDone }: { rental: Rental; onClose: () => void; onDone: () => void }) {
  const activeLines = (rental.lines || []).filter((l) => l.active && l.asset_id);
  const [selected, setSelected] = useState<string[]>(activeLines.map((l) => l.asset_id!));
  const [hours, setHours] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [close, setClose] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allSelected = selected.length === activeLines.length;

  function toggle(id: string) {
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  async function submit() {
    if (selected.length === 0) { setErr('Elige al menos un equipo que regresa.'); return; }
    setSaving(true); setErr(null);
    try {
      const hm: Record<string, number> = {};
      for (const id of selected) {
        const v = hours[id];
        if (v !== undefined && v !== '' && !Number.isNaN(Number(v))) hm[id] = Number(v);
      }
      await returnRental(rental.id, {
        // Vacío significa "toda la renta" para el backend; mandamos la lista
        // explícita solo cuando es parcial, para no depender de ese default.
        asset_ids: allSelected ? [] : selected,
        note: note.trim(),
        close,
        hour_meters: hm,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo registrar el regreso');
    } finally { setSaving(false); }
  }

  const field = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-lg font-semibold text-foreground">Regreso de equipo</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Renta de {rental.client_name || 'cliente'}. Los equipos vuelven al inventario como disponibles.
          Captura el horómetro ahora que tienes la máquina enfrente.
        </p>

        <div className="space-y-2">
          {activeLines.map((l) => {
            const id = l.asset_id!;
            const on = selected.includes(id);
            return (
              <div key={id} className={`rounded-md border p-3 ${on ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={on} onChange={() => toggle(id)} />
                  <span className="text-sm font-medium text-foreground">{l.asset_name}</span>
                </label>
                {on ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Horómetro</span>
                    <input
                      type="number" min={0} step="0.1"
                      className="w-32 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                      value={hours[id] ?? ''}
                      onChange={(e) => setHours((p) => ({ ...p, [id]: e.target.value }))}
                      placeholder="horas"
                    />
                    <span className="text-xs text-muted-foreground">opcional</span>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-3 space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Nota (daños, faltantes, observaciones)</label>
            <textarea className={field} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} />
            Cerrar la renta si ya no queda equipo afuera
          </label>

          {err ? <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div> : null}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted">Cancelar</button>
            <button onClick={submit} disabled={saving} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {saving ? 'Guardando…' : 'Registrar regreso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
